import { Switch, Route, Redirect, Router as WouterRouter, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/authContext";
import { isAdminUser } from "@/lib/admin";
import { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import HomePage from "@/pages/home";
import RoutesPage from "@/pages/routes";
import RouteDetailPage from "@/pages/route-detail";
import RouteBuilderPage from "@/pages/route-builder";
import MyRoutesPage from "@/pages/routes/my-routes";
import FavoriteRoutesPage from "@/pages/routes/favorites";
import WishlistRoutesPage from "@/pages/routes/wishlist";
import BrowseRoutesPage from "@/pages/routes/browse";
import SearchRoutesPage from "@/pages/routes/search";
import ShopsPage from "@/pages/shops";
import ShopDetailPage from "@/pages/shop-detail";
import CommunityPage from "@/pages/community";
import OwnersSelectionPage from "@/pages/owners-selection";
import OwnersKringloopPage from "@/pages/owners-kringloop";
import AccountPage from "@/pages/account";
import AccountDeletedPage from "@/pages/account-deleted";
import ConfirmDeletePage from "@/pages/confirm-delete";
import AddShopPage from "@/pages/add-shop";
import LoginPage from "@/pages/login";
import ResetPasswordPage from "@/pages/reset-password";
import FavoriteShopsPage from "@/pages/shops/favorites";
import WishlistShopsPage from "@/pages/shops/wishlist";
import ClaimShopPage from "@/pages/claim-shop";
import WishlistPage from "@/pages/wishlist";
import ConfirmedPage from "@/pages/confirmed";
import WishlistSharedPage from "@/pages/wishlist-shared";
import NotFound from "@/pages/not-found";
import VerificationSuccessPage from "@/pages/owner/verification-success";
import UnauthorizedPage from "@/pages/unauthorized";
import PublicProfilePage from "@/pages/public-profile";
import { CookieBanner } from "@/components/CookieBanner";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { useRestyleVisibility } from "@/lib/featureFlags";
import { APP_LOCALES, detectPreferredLocale, getLocaleFromPath, I18nProvider, stripLocalePrefix, type AppLocale, withLocalePrefix, type TranslationOverrides, isAppLocale } from "@/lib/i18n";
import { useQuery } from "@tanstack/react-query";
import { getLocalizedPath, useI18n } from "@/lib/i18n";
import MaintenanceWrapper from "@/components/MaintenanceWrapper";
import { useSessionTracker } from "@/hooks/useSessionTracker";
import { ShopInteractionsProvider } from "@/contexts/ShopInteractionsContext";
import { RouteInteractionsProvider } from "@/contexts/RouteInteractionsContext";
import { GoogleMapsProvider } from "@/contexts/GoogleMapsContext";
import { RouteBuilderProvider } from "@/contexts/RouteBuilderContext";
import { Header } from "@/components/Header";

const ShopReviewsPage = lazy(() => import("@/pages/shop-reviews"));
const ShopPostsPage = lazy(() => import("@/pages/shop-posts"));
const BackofficeDashboard = lazy(() => import("@/pages/backoffice"));
const BackofficeSEOStatus = lazy(() => import("@/pages/backoffice/seo-status"));
const BackofficeShops = lazy(() => import("@/pages/backoffice/shops"));
const BackofficeVerifications = lazy(() => import("@/pages/backoffice/verifications"));
const BackofficeBatchJobs = lazy(() => import("@/pages/backoffice/batch-jobs"));
const BackofficeUsers = lazy(() => import("@/pages/backoffice/users"));
const BackofficeCron = lazy(() => import("@/pages/backoffice/cron"));
const BackofficeRoutes = lazy(() => import("@/pages/backoffice/routes"));
const BackofficePosts = lazy(() => import("@/pages/backoffice/posts"));
const BackofficeBilling = lazy(() => import("@/pages/backoffice/billing"));
const BackofficeAPI = lazy(() => import("@/pages/backoffice/api"));
const BackofficeLog = lazy(() => import("@/pages/backoffice/log"));
const BackofficeRouteAnalytics = lazy(() => import("@/pages/backoffice/route-analytics"));
const BackofficePerformanceSnapshot = lazy(() => import("@/pages/backoffice/performance-snapshot"));
const BackofficeModeration = lazy(() => import("@/pages/backoffice/moderation"));
const BackofficeScannerPromptsPage = lazy(() => import("@/pages/backoffice/scanner"));
const BackofficeTranslationsPage = lazy(() => import("@/pages/backoffice/translations"));
const BackofficePriceIdPage = lazy(() => import("@/pages/backoffice/price-id"));
const BackofficeProvinceFix = lazy(() => import("@/pages/backoffice/fix-provinces"));
const BackofficeRegionsFill = lazy(() => import("@/pages/backoffice/regions-fill"));
const BackofficePrivacy = lazy(() => import("@/pages/backoffice/privacy"));
const BackofficeImportTroc = lazy(() => import("@/pages/backoffice/import-troc"));
const BackofficeOSM = lazy(() => import("@/pages/backoffice/osm"));
const BackofficeRoutingConfig = lazy(() => import("@/pages/backoffice/routing-config"));
const ScannerPage = lazy(() => import("@/pages/scanner"));
const ScanResultsPage = lazy(() => import("@/pages/scanner/results"));
const MyScansPage = lazy(() => import("@/pages/scanner/history"));
const RestylePage = lazy(() => import("@/pages/restyle"));
const RestyleHistoryPage = lazy(() => import("@/pages/restyle/history"));
const RestyleProjectPage = lazy(() => import("@/pages/restyle-project"));
const SpaceBlendIndex = lazy(() => import("@/pages/spaceblend/index"));
const SpaceBlendEditor = lazy(() => import("@/pages/spaceblend/editor"));
const SpaceBlendProject = lazy(() => import("@/pages/spaceblend/project"));
const SpaceBlendArchive = lazy(() => import("@/pages/spaceblend/archive"));
const FAQPage = lazy(() => import("@/pages/legal/faq"));
const PrivacyPage = lazy(() => import("@/pages/legal/privacy"));
const TermsPage = lazy(() => import("@/pages/legal/terms"));
const DisclaimerPage = lazy(() => import("@/pages/legal/disclaimer"));
const ContactPage = lazy(() => import("@/pages/contact"));
const AboutPage = lazy(() => import("@/pages/about"));
const SimpleMapTest = lazy(() => import("@/components/SimpleMapTest"));
const DebugMaps = lazy(() => import("@/pages/DebugMaps"));
const TestMapIcons = lazy(() => import("@/pages/TestMapIcons"));

function AppRoutes() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { canSeeRestyle } = useRestyleVisibility();
  const { locale } = useI18n();
  const localizedRoutesPath = getLocalizedPath("routes", locale);
  const localizedRouteDetailPath = getLocalizedPath("routeDetail", locale);
  const localizedShopsPath = getLocalizedPath("shops", locale);
  const localizedCommunityPath = getLocalizedPath("community", locale);
  const localizedScannerPath = getLocalizedPath("scanner", locale);
  const localizedRestylePath = getLocalizedPath("restyle", locale);
  const localizedSpaceBlendPath = getLocalizedPath("spaceblend", locale);

  return (
    <Suspense fallback={<div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">Loading...</div>}>
      <Switch>
      {/* Backoffice Routes - Must come before dynamic routes */}
      <Route path="/backoffice" component={BackofficeDashboard} />
      <Route path="/backoffice/seo-status" component={BackofficeSEOStatus} />
      <Route path="/backoffice/batch-jobs" component={BackofficeBatchJobs} />
      <Route path="/backoffice/verifications" component={BackofficeVerifications} />
      <Route path="/backoffice/shops" component={BackofficeShops} />
      <Route path="/backoffice/users" component={BackofficeUsers} />
      <Route path="/backoffice/routes" component={BackofficeRoutes} />
      <Route path="/backoffice/posts" component={BackofficePosts} />
      <Route path="/backoffice/billing" component={BackofficeBilling} />
      <Route path="/backoffice/cron" component={BackofficeCron} />
      <Route path="/backoffice/api" component={BackofficeAPI} />
      <Route path="/backoffice/privacy" component={BackofficePrivacy} />
      <Route path="/backoffice/route-analytics" component={BackofficeRouteAnalytics} />
      <Route path="/backoffice/performance-snapshot" component={BackofficePerformanceSnapshot} />
      <Route path="/backoffice/log" component={BackofficeLog} />
      <Route path="/backoffice/moderation" component={BackofficeModeration} />
      <Route path="/backoffice/shops-moderation"><Redirect to="/backoffice/moderation" /></Route>
      <Route path="/backoffice/reports"><Redirect to="/backoffice/moderation" /></Route>
      <Route path="/backoffice/suggestions"><Redirect to="/backoffice/moderation" /></Route>
      <Route path="/backoffice/scanner" component={BackofficeScannerPromptsPage} />
      <Route path="/backoffice/translations" component={BackofficeTranslationsPage} />
      <Route path="/backoffice/price-id" component={BackofficePriceIdPage} />
      <Route path="/backoffice/fix-provinces" component={BackofficeProvinceFix} />
      <Route path="/backoffice/regions-fill" component={BackofficeRegionsFill} />
      <Route path="/backoffice/import-shops" component={BackofficeImportTroc} />
      <Route path="/backoffice/osm" component={BackofficeOSM} />
      <Route path="/backoffice/routing-config" component={BackofficeRoutingConfig} />

      <Route path="/" component={HomePage} />
      <Route path="/routes" component={RoutesPage} />
      <Route path={localizedRoutesPath} component={RoutesPage} />
      <Route path="/routes/my-routes" component={MyRoutesPage} />
      <Route path="/routes/favorites" component={FavoriteRoutesPage} />
      <Route path="/routes/wishlist" component={WishlistRoutesPage} />
      <Route path="/routes/browse" component={BrowseRoutesPage} />
      <Route path="/routes/search" component={SearchRoutesPage} />
      <Route path="/routes/new" component={RouteBuilderPage} />
      <Route path="/routes/edit/:id" component={RouteBuilderPage} />
      {/* SEO-friendly routes */}
      <Route path="/kringlooproutes/:slug" component={RouteDetailPage} />
      <Route path={`${localizedRouteDetailPath}/:slug`} component={RouteDetailPage} />
      <Route path="/shops/favorites" component={FavoriteShopsPage} />
      <Route path="/shops/wishlist" component={WishlistShopsPage} />
      <Route path="/kringloopwinkels" component={ShopsPage} />
      <Route path={localizedShopsPath} component={ShopsPage} />
      <Route path="/kringloopwinkels/voeg-toe" component={AddShopPage} />
      <Route path="/kringloopwinkels/:province" component={ShopsPage} />
      <Route path={`${localizedShopsPath}/:province`} component={ShopsPage} />

      <Route path="/kringloopwinkels/:province/:city/:name" component={ShopDetailPage} />
      <Route path={`${localizedShopsPath}/:province/:city/:name`} component={ShopDetailPage} />
      {/* Country-specific shop profile URLs */}
      <Route path="/kringloopwinkel/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/thrift-store/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/second-hand-shop/:province/:city/:name" component={ShopDetailPage} />

      {/* New SEO routes with country prefix: /country/type/province/city/name */}
      <Route path="/:country/kringloopwinkel/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/:country/thrift-store/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/:country/second-hand-shop/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/:country/magasin-doccasion/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/:country/second-hand-laden/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/:country/tienda-de-segunda-mano/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/:country/negozio-di-usato/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/:country/genbrugsbutik/:province/:city/:name" component={ShopDetailPage} />
      <Route path="/:country/second-hand-butik/:province/:city/:name" component={ShopDetailPage} />

      {/* Localized collection routes with country: /country/type */}
      <Route path="/:country/kringloopwinkels" component={ShopsPage} />
      <Route path="/:country/thrift-stores" component={ShopsPage} />
      <Route path="/:country/second-hand-shops" component={ShopsPage} />
      <Route path="/:country/magasins-doccasion" component={ShopsPage} />
      <Route path="/:country/second-hand-laeden" component={ShopsPage} />
      <Route path="/:country/tiendas-de-segunda-mano" component={ShopsPage} />
      <Route path="/:country/negozi-di-usato" component={ShopsPage} />
      <Route path="/:country/shops" component={ShopsPage} />
      {/* Legal Routes */}
      <Route path="/faq" component={FAQPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/voorwaarden" component={TermsPage} />
      <Route path="/disclaimer" component={DisclaimerPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/over-vintmap" component={AboutPage} />

      {/* Legacy routes for backwards compatibility */}

      <Route path="/routes/:id" component={RouteDetailPage} />
      <Route path="/shops/:id" component={ShopDetailPage} />
      <Route path="/shops/:id/reviews" component={ShopReviewsPage} />
      <Route path="/shops/:id/posts" component={ShopPostsPage} />
      <Route path="/community" component={CommunityPage} />
      <Route path={localizedCommunityPath} component={CommunityPage} />
      <Route path="/owners" component={OwnersSelectionPage} />
      <Route path="/owners/kringloop" component={OwnersKringloopPage} />
      <Route path="/claim-shop/:id" component={ClaimShopPage} />
      <Route path="/login" component={LoginPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/account" component={AccountPage} />
      <Route path="/u/:username" component={PublicProfilePage} />
      {/* Shortened profile link */}
      <Route path="/@:username" component={(props: any) => <Redirect to={`/u/${props.params.username}`} />} />
      <Route path="/account-deleted" component={AccountDeletedPage} />
      <Route path="/account/confirm-delete" component={ConfirmDeletePage} />
      <Route path="/scanner" component={isAdmin ? ScannerPage : NotFound} />
      <Route path={localizedScannerPath} component={isAdmin ? ScannerPage : NotFound} />
      <Route path="/scanner/results/:id" component={isAdmin ? ScanResultsPage : NotFound} />
      <Route path={`${localizedScannerPath}/results/:id`} component={isAdmin ? ScanResultsPage : NotFound} />
      <Route path="/scanner/history" component={isAdmin ? MyScansPage : NotFound} />
      <Route path={`${localizedScannerPath}/history`} component={isAdmin ? MyScansPage : NotFound} />
      <Route path="/mijn-scans" component={isAdmin ? MyScansPage : NotFound} />
      <Route path="/restyle" component={canSeeRestyle ? RestylePage : NotFound} />
      <Route path={localizedRestylePath} component={canSeeRestyle ? RestylePage : NotFound} />
      <Route path="/restyle/history" component={canSeeRestyle ? RestyleHistoryPage : NotFound} />
      <Route path="/restyle/project/:id" component={canSeeRestyle ? RestyleProjectPage : NotFound} />
      <Route path={`${localizedRestylePath}/project/:id`} component={canSeeRestyle ? RestyleProjectPage : NotFound} />

      {/* SpaceBlend Routes (Admin Exclusive) */}
      <Route path="/spaceblend" component={isAdmin ? SpaceBlendIndex : NotFound} />
      <Route path={localizedSpaceBlendPath} component={isAdmin ? SpaceBlendIndex : NotFound} />
      <Route path="/spaceblend/editor" component={isAdmin ? SpaceBlendEditor : NotFound} />
      <Route path={`${localizedSpaceBlendPath}/editor`} component={isAdmin ? SpaceBlendEditor : NotFound} />
      <Route path="/spaceblend/project/:id" component={isAdmin ? SpaceBlendProject : NotFound} />
      <Route path={`${localizedSpaceBlendPath}/project/:id`} component={isAdmin ? SpaceBlendProject : NotFound} />
      <Route path="/spaceblend/archive" component={isAdmin ? SpaceBlendArchive : NotFound} />
      <Route path={`${localizedSpaceBlendPath}/archive`} component={isAdmin ? SpaceBlendArchive : NotFound} />
      <Route path="/confirmed" component={ConfirmedPage} />
      <Route path="/wishlist" component={WishlistPage} />
      <Route path="/wishlist/shared/:token" component={WishlistSharedPage} />
      <Route path="/owner/verification-success" component={VerificationSuccessPage} />
      <Route path="/unauthorized" component={UnauthorizedPage} />
      <Route path="/test-map" component={SimpleMapTest} />
      <Route path="/debug-maps" component={DebugMaps} />
      <Route path="/test-map-icons" component={TestMapIcons} />
      <Route component={NotFound} />
    </Switch >
    </Suspense>
  );
}

function ScrollToTop() {
  const [location] = useLocation();
  const prevLocation = useRef(location);

  useEffect(() => {
    if (location !== prevLocation.current) {
      window.scrollTo(0, 0);
      prevLocation.current = location;
    }
  }, [location]);

  return null;
}

function LocaleSeoHead({ activeLocale }: { activeLocale: AppLocale }) {
  const [location] = useLocation();

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    document.documentElement.lang = activeLocale;

    const strippedPath = stripLocalePrefix(location || window.location.pathname);
    const origin = window.location.origin;
    const canonicalHref = `${origin}${withLocalePrefix(strippedPath, activeLocale)}`;

    const upsertLink = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"][data-vintmap-seo="1"]`
        : `link[rel="${rel}"][data-vintmap-seo="1"]`;
      let link = document.head.querySelector(selector) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        link.setAttribute("data-vintmap-seo", "1");
        if (hreflang) link.setAttribute("hreflang", hreflang);
        document.head.appendChild(link);
      }
      link.href = href;
    };

    upsertLink("canonical", canonicalHref);
    for (const locale of APP_LOCALES) {
      upsertLink("alternate", `${origin}${withLocalePrefix(strippedPath, locale)}`, locale);
    }
    upsertLink("alternate", `${origin}${withLocalePrefix(strippedPath, "en")}`, "x-default");
  }, [activeLocale, location]);

  return null;
}



function ConditionalGoogleMapsProvider({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();

  const needsMap = useMemo(() => {
    // Routes that DEFINITELY need or might need Google Maps (Interactive maps or Autocomplete)
    const routesWithMaps = [
      '/routes/',           // Route Details, Builder, Editor
      '/kringlooproutes/',  // SEO Route Details
      '/kringloopwinkels/voeg-toe', // Add Shop
      '/claim-shop',        // Claim Shop
      '/account',           // Set Home Address (Autocomplete)
      '/test-map',          // Dev tools
      '/debug-maps',
      '/test-map-icons'
    ];

    // Check if current location starts with any of the map routes
    // We normalize location by stripping trailing slashes
    const path = location || "/";
    return routesWithMaps.some(route => path.startsWith(route));
  }, [location]);

  if (!needsMap) {
    return <>{children}</>;
  }

  return <GoogleMapsProvider>{children}</GoogleMapsProvider>;
}



function AppContainer() {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const localeFromPath = getLocaleFromPath(pathname);
  const activeLocale: AppLocale = localeFromPath || detectPreferredLocale();

  const { data: translationOverrides = {} } = useQuery<TranslationOverrides>({
    queryKey: ["public-translations-overrides"],
    queryFn: async () => {
      const response = await fetch("/api/features/translations");
      if (!response.ok) return {};
      const json = await response.json().catch(() => null);
      return (json?.overrides || {}) as TranslationOverrides;
    },
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Fetch Public Config (API Keys)
  useEffect(() => {
    fetch("/api/config/public")
      .then(res => res.json())
      .then(config => {
        (window as any).VINTMAP_CONFIG = config;
        console.log("[App] Public config loaded");
        console.log("MAPTILER_KEY status:", config.mapTilerKey ? "FOUND" : "MISSING");
        if (import.meta.env.DEV) {
          console.log("MAPTILER_KEY (preview):", config.mapTilerKey?.substring(0, 5) + "...");
        }
      })
      .catch(err => console.error("[App] Failed to load public config:", err));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // IMPORTANT: If we are in the default locale (nl) AND the path has NO locale prefix,
    // we should NOT redirect to /nl/... unless we explicitly want to force prefixes.
    // Currently, VintMap prefers no prefix for NL.
    const currentPath = window.location.pathname || "/";
    const hasPrefix = isAppLocale(currentPath.split("/").filter(Boolean)[0]);

    if (activeLocale === "nl" && !hasPrefix) {
      // Already on the correct "no-prefix" path for NL
      return;
    }

    const target = withLocalePrefix(currentPath, activeLocale);
    if (target === currentPath) return;

    const search = window.location.search || "";
    const hash = window.location.hash || "";
    window.location.replace(`${target}${search}${hash}`);
  }, [activeLocale, pathname]);

  const routerBase = useMemo(() => (activeLocale === "nl" ? "" : `/${activeLocale}`), [activeLocale]);
  const isDefaultLocaleWithoutPrefix = !localeFromPath && activeLocale === "nl";

  // Relaxed guard: we always want to render the I18nProvider and Router
  // to allow the internal redirect or the NotFound component to handle the request.
  // Returning null here causes a blank screen on valid deep links that just need better detection.

  return (
    <I18nProvider locale={activeLocale} overrides={translationOverrides}>
      <WouterRouter base={routerBase}>
        <ScrollToTop />
        <LocaleSeoHead activeLocale={activeLocale} />
        <SessionTrackerMount />

        <ShopInteractionsProvider>
          <RouteInteractionsProvider>
            <RouteBuilderProvider>
              <MaintenanceWrapper>
                <Toaster />
                <CookieBanner />
                <PWAInstallPrompt />
                <div className="min-h-screen flex flex-col overflow-x-hidden">
                  <Header />
                  <main className="flex-1">
                    <ConditionalGoogleMapsProvider>
                      <AppRoutes />
                    </ConditionalGoogleMapsProvider>
                  </main>
                </div>
              </MaintenanceWrapper>
            </RouteBuilderProvider>
          </RouteInteractionsProvider>
        </ShopInteractionsProvider>
      </WouterRouter>
    </I18nProvider>
  );
}

/** Thin wrapper so the hook mounts once inside the Router/Auth tree */
function SessionTrackerMount() {
  useSessionTracker();
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <AppContainer />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
